// @ts-nocheck
import React, { useState } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import {
  CheckIcon, ChevronUpIcon, GripVerticalIcon,
  BellIcon, XmarkIcon, PlusIcon,
  CircleCheckIcon, CircleInfoIcon
} from '@revfluence/fresh-icons/regular/esm';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { cn } from '@frontend/shadcn/lib/utils';
import { useCustomStages } from '../context/CustomStagesContext';

// Keep these for consistent UI
const STATUS_COLORS = {
  'Wait for Review': 'bg-sky-200',
  'Review Needed': 'bg-amber-100',
  'Review Proposal': 'bg-amber-100',
  'Approved': 'bg-emerald-100',
  'Rejected': 'bg-red-200',
  'Test Status 1': 'bg-sky-200',
  'Test Status 2': 'bg-amber-100',
  'Test Status 3': 'bg-emerald-100',
  'Test Status 4': 'bg-red-200',
  'Custom Status 1': 'bg-sky-200',
  'Custom Status 2': 'bg-amber-100',
  'Custom Status 3': 'bg-emerald-100',
  'Custom Status 4': 'bg-red-200',
  '11': 'bg-sky-200',
  '12': 'bg-amber-100',
  '13': 'bg-emerald-100',
  '14': 'bg-red-200',
  'Status 1': 'bg-sky-200',
  'Status 2': 'bg-amber-100',
  'Status 3': 'bg-emerald-100',
  'Status 4': 'bg-red-200',
};

interface NewStatus {
  name: string;
  isNew?: boolean;
}

interface Props {
  statuses: Array<{ id: number; name: string; priority: number }>;
  setStatuses: (statuses: Array<{ id: number; name: string; priority: number }>) => void;
}

export const StatusSection: React.FC<Props> = ({ statuses, setStatuses }) => {
  const { selectedStage, updateStage, isNewStage } = useCustomStages();
  const [isExpanded, setIsExpanded] = useState(true);
  const [newStatus, setNewStatus] = useState<NewStatus | null>(null);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedStage.statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update priorities based on new order
    const updatedStatuses = items.map((status, index) => ({
      ...status,
      priority: index + 1
    }));

    updateStage(selectedStage.id, { statuses: updatedStatuses });
  };

  const handleDragEndNewStatus = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStatuses(items);
  };

  const handleDeleteStatus = (statusId: number) => {
    if (isNewStage) {
      setStatuses(statuses.filter((status) => status.id !== statusId));
    } else {
      const updatedStatuses = selectedStage.statuses.filter((status) => status.id !== statusId);
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
  };

  const handleAddStatus = () => {
    setNewStatus({ name: '', isNew: true });
  };

  const handleSaveNewStatus = () => {
    if (!newStatus?.name.trim()) return;

    if (isNewStage) {
      // Use the props setter instead of local state
      setStatuses([...statuses, {
        id: Date.now(),
        name: newStatus.name,
        priority: statuses.length + 1
      }]);
    } else {
      // For existing stage, update through context
      const updatedStatuses = [...(selectedStage?.statuses || []), {
        id: Date.now(),
        name: newStatus.name,
        priority: selectedStage?.statuses?.length + 1 || 1
      }];
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
    setNewStatus(null);
  };

  const handleCancelNewStatus = () => {
    setNewStatus(null);
  };

  // Show empty state if it's a new stage
  if (isNewStage) {
    return (
      <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 p-1 bg-blue-6 rounded-lg flex items-center justify-center">
              <CheckIcon className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="flex-1 text-sm font-medium text-gray-800">Status</span>
            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ChevronUpIcon className={cn('w-3.5 h-3.5 text-gray-800 transition-transform', !isExpanded && 'rotate-180')} />
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Add and define statuses to monitor the progress of brand and member actions within this custom stage.
          </p>
        </div>

        {isExpanded && (
          <DragDropContext onDragEnd={handleDragEndNewStatus}>
            <Droppable droppableId="statuses">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {/* Show new stage statuses */}
                  {statuses.map((status, index) => (
                    <Draggable
                      key={status.id.toString()}
                      draggableId={status.id.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            'h-12 p-2 rounded-xl border bg-white border-gray-200 flex items-center gap-1',
                            snapshot.isDragging && 'opacity-50'
                          )}
                        >
                          <div {...provided.dragHandleProps}>
                            <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                          </div>
                          <div className="flex-1 flex items-center gap-2">
                            <div className="p-1 bg-white rounded-lg border border-gray-200">
                              <div className={`w-6 h-6 rounded-md border border-gray-200 ${STATUS_COLORS[status.name] || 'bg-sky-100'}`} />
                            </div>
                            <span className="flex-1 text-sm font-medium text-gray-800">{status.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                              onClick={() => handleDeleteStatus(status.id)}
                            >
                              <XmarkIcon className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* New status input */}
        {newStatus && (
        <div className="h-12 p-2 rounded-xl border bg-white border-gray-200 flex items-center gap-1">
          <GripVerticalIcon className="w-3 h-3 text-gray-300" />
          <div className="flex-1 flex items-center gap-2">
            <div className="p-1 bg-white rounded-lg border border-gray-200">
              <div className="w-6 h-6 rounded-md border border-gray-200 bg-sky-100" />
            </div>
            <Input
              value={newStatus.name}
              onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
              className="flex-1 h-8 px-3 py-1 text-sm"
              placeholder="Enter status name"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
              onClick={() => setNewStatus(null)}
            >
              <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
              onClick={handleSaveNewStatus}
            >
              <CheckIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
          </div>
        </div>
            )}

        <button
          className="h-6 flex items-center gap-2 text-sm font-medium text-gray-800 mt-6"
          onClick={() => setNewStatus({ name: '', isNew: true })}
          disabled={!!newStatus}
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add Status
        </button>
      </div>
        );
}

  return (
    <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 p-1 bg-blue-6 rounded-lg flex items-center justify-center">
            <CheckIcon className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="flex-1 text-sm font-medium text-gray-800">Status</span>
          <button
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ChevronUpIcon className={cn('w-3.5 h-3.5 text-gray-800 transition-transform', !isExpanded && 'rotate-180')} />
          </button>
        </div>
        <p className="text-sm text-gray-500">
          Add and define statuses to monitor the progress of brand and member actions within this custom stage.
        </p>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-2">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="statuses">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {selectedStage?.statuses?.map((status, index) => (
                    <Draggable
                      key={status.id.toString()}
                      draggableId={status.id.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            'h-12 p-2 rounded-xl border bg-white border-gray-200 flex items-center gap-1',
                            snapshot.isDragging && 'opacity-50'
                          )}
                        >
                          <div {...provided.dragHandleProps}>
                            <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                          </div>

                          <div className="flex-1 flex items-center gap-2">
                            <div className="p-1 bg-white rounded-lg border border-gray-200">
                              <div className={`w-6 h-6 rounded-md border border-gray-200 ${STATUS_COLORS[status.name] || 'bg-sky-100'}`} />
                            </div>
                            <span className="flex-1 text-sm font-medium text-gray-800">{status.name}</span>
                          </div>

                          <div className="flex items-center gap-1">
                            <button className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
                              <CircleCheckIcon className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                            <button className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
                              <BellIcon className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                            <button
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                              onClick={() => handleDeleteStatus(status.id)}
                            >
                              <XmarkIcon className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {newStatus && (
            <div className="h-12 p-2 rounded-xl border bg-white border-gray-200 flex items-center gap-1">
              <GripVerticalIcon className="w-3 h-3 text-gray-300" />

              <div className="flex-1 flex items-center gap-2">
                <div className="p-1 bg-white rounded-lg border border-gray-200">
                  <div className="w-6 h-6 rounded-md border border-gray-200 bg-sky-100" />
                </div>
                <Input
                  value={newStatus.name}
                  onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                  className="flex-1 h-8 px-3 py-1 text-sm"
                  placeholder="Enter status name"
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-1">
                <button
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                  onClick={handleCancelNewStatus}
                >
                  <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                </button>
                <button
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                  onClick={handleSaveNewStatus}
                >
                  <CheckIcon className="w-3.5 h-3.5 text-gray-800" />
                </button>
              </div>
            </div>
          )}

          <div className="p-2 bg-gray-50 rounded-xl border border-gray-200 flex items-center gap-1">
            <div className="opacity-0">
              <GripVerticalIcon className="w-3 h-3" />
            </div>
            <span className="flex-1 text-sm font-medium text-gray-400">All</span>
            <CircleInfoIcon className="w-5 h-5 text-gray-300" />
          </div>

          <button
            className="h-6 flex items-center gap-2 text-sm font-medium text-gray-800 mt-6"
            onClick={handleAddStatus}
            disabled={!!newStatus}
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add Status
          </button>
        </div>
      )}
    </div>
  );
};
